// (Copyright) Confluent, Inc.

import type { ReactNode } from 'react';
import { css, type DefaultTheme } from 'styled-components';
import { type ColorToken } from '@confluent/axon';

import { fontBasedStyles, createCapBasedStyledComponent } from '../../utils/typefaces';
import { SF, INTER } from '../../constants/typefaces';
import { type Color } from '../../constants/colors';
import { withDisableStyleOverrides } from '../../utils/disableOverride';
import { cssColor } from '../../utils/color';
import { A } from '../A';

// children needs to be optional to allow these components to work with CTrans
type Props = {
  ['data-testid']?: string;
  children?: ReactNode;
  color?: Color;
  colorToken?: ColorToken;
};

export const colorProp =
  (defaultColorToken: ColorToken) =>
  (props: { color?: Color; colorToken?: ColorToken; theme: DefaultTheme }) => {
    const colorString = props.color
      ? cssColor(props.color)
      : props.theme.colors[props.colorToken ?? defaultColorToken];

    if (colorString) {
      return `color: ${colorString};`;
    }

    return '';
  };

export const H1 = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('h1', {
  capHeight: 34,
  lineGap: 22,
})`
  ${colorProp('text_primary')};
  font-weight: 200;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 1.15px;`,
    [INTER]: `letter-spacing: -2px;`,
  })}
`);

export const H2 = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('h2', {
  capHeight: 22,
  lineGap: 18,
})`
  ${colorProp('text_primary')};
  font-weight: 700;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.15px;`,
    [INTER]: `letter-spacing: -1px;`,
  })}
`);

export const H3 = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('h3', {
  capHeight: 17,
  lineGap: 15,
})`
  ${colorProp('text_primary')};
  font-weight: 600;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.15px;`,
    [INTER]: `letter-spacing: -1px;`,
  })}
`);

const h4CapConfig = {
  capHeight: 13,
  lineGap: 11,
};
const h4Styles = css`
  ${colorProp('text_primary')};
  font-weight: 600;

  ${fontBasedStyles({
    [SF]: `letter-spacing: -0.1px;`,
    [INTER]: `letter-spacing: -0.5px;`,
  })}
`;
export const H4 = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('h4', h4CapConfig)`
  ${h4Styles}
`);

export const H4LikeDiv = withDisableStyleOverrides<Props>(createCapBasedStyledComponent(
  'div',
  h4CapConfig
)`
${h4Styles}
`);

export const Strong = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('strong', {
  capHeight: 10,
  lineGap: 12,
})`
  ${colorProp('text_primary')};
  font-weight: 600;

  ${fontBasedStyles({
    [SF]: `letter-spacing: -0.1px;`,
    [INTER]: `letter-spacing: -0.5px;`,
  })}
`);

export const Body = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('span', {
  capHeight: 10,
  lineGap: 12,
})`
  ${colorProp('text_primary')};
  font-weight: 300;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0px;`,
  })}
`);

export const BodyUppercase = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('div', {
  capHeight: 9,
  lineGap: 17,
})`
  ${colorProp('text_primary')};
  font-weight: 300;
  text-transform: uppercase;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0px;`,
  })}
`);

export const Code = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('span', {
  capHeight: 9,
  lineGap: 9,
})`
  ${colorProp('text_primary')};
  font-weight: normal;
  letter-spacing: 0.25px;
  font-family: ${({ theme }) => theme.fonts.mono};
  display: inline-block;
`);

export const BreadcrumbText = withDisableStyleOverrides<Props>(createCapBasedStyledComponent(
  'span',
  {
    capHeight: 9,
    lineGap: 5,
  }
)`
  ${colorProp('text_primary')};
  font-weight: 600;
  text-transform: uppercase;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.65px;`,
    [INTER]: 'letter-spacing: 1.15px;',
  })}
`);

export const Link = withDisableStyleOverrides<Props>(createCapBasedStyledComponent(A, {
  capHeight: 10,
  lineGap: 12,
})`
  ${colorProp('link_primary')};
  font-weight: 400;
  text-decoration: underline;
  display: inline-block;

  &:hover {
    ${colorProp('link_primary_hover')};
  }

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0px;`,
  })}
`);

export const LinkLike = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('span', {
  capHeight: 10,
  lineGap: 12,
})`
  ${colorProp('text_primary')};
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;

  &:hover {
    ${colorProp('text_primary')};
  }

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0px;`,
  })}
`);

export const Tiny = withDisableStyleOverrides<Props>(createCapBasedStyledComponent('span', {
  capHeight: 7,
  lineGap: 11,
})`
  ${colorProp('text_primary')};
  font-weight: 500;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.15px;`,
    [INTER]: 'letter-spacing: 0.15px;',
  })}
 `);

export const Typefaces = {
  H1,
  H2,
  H3,
  H4,
  Strong,
  Body,
  BodyUppercase,
  Code,
  BreadcrumbText,
  Link,
  LinkLike,
  Tiny,
};
